import React, { useState, useContext } from "react";
import { DataGrid, GridColDef, GridColumnResizeParams } from "@mui/x-data-grid";
import {
  Box,
  Checkbox,
  Button,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BASE_PASTEL_COLORS } from "../utils/batchColor";
import { UserContext } from "../components/app-routes";

type ColumnOption = {
  field: string;
  label: string;
};

type TaskGridProps = {
  rows: any[];
  baseColumns: GridColDef[];
  columnOptions: ColumnOption[];
  taskKey: string;
  rowClassName?: (params: any) => string;
  rowStyle?: (params: any) => React.CSSProperties; // ← new
};

const gridStyle = {
  fontSize: "0.9rem",

  // ✅ Header row styling
  "& .MuiDataGrid-columnHeaders": {
    minHeight: "60px !important",
    maxHeight: "none",
    borderBottom: "1px solid #d0d0d0",
  },
  "& .MuiDataGrid-columnHeader": {
    paddingTop: "6px",
    backgroundColor: "#ecebebff",
    paddingBottom: "6px",
    alignItems: "center",
    minHeight: "60px !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    lineHeight: 1.4,
    fontSize: "0.9rem",
    textAlign: "center",
    wordBreak: "break-word",
  },

  "& .MuiDataGrid-columnSeparator": {
    visibility: "visible",
    opacity: 1,
    "& .MuiSvgIcon-root": {
      color: "rgba(127, 126, 126, 0.51)",
    },
  },

  // ✅ Vertically center row contents
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    whiteSpace: "normal",
    wordWrap: "break-word",
    lineHeight: 1.35,
  },
  "& .MuiDataGrid-cellContent": {
    display: "flex",
    alignItems: "center",
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
  },

  // ✅ Font sizes + icons
  "& .MuiDataGrid-cell *:not(.MuiCheckbox-root)": {
    fontSize: "0.9rem",
  },
  "& .MuiDataGrid-cell a": {
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.3,
  },
  "& .MuiCheckbox-root .MuiSvgIcon-root": {
    fontSize: 24,
  },
  "& .MuiDataGrid-cell .MuiIconButton-root .MuiSvgIcon-root": {
    fontSize: 22, // pencil & other icons in cells
  },

  // Hide sort + menu icons (we’re doing simple headers)
  "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
    display: "none",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f5f5f5", // light grey on hover
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "#ffffff !important",
  },
  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "#f5f5f5 !important", // selected + hover = same grey
  },
  "& .overdue-row": {
    backgroundColor: "#ed6e6e86 !important",
  },
  "& .MuiDataGrid-row.batch-colored-row": {
    backgroundColor: "var(--batch-color) !important",
  },
};

type HeaderProps = {
  colDef: GridColDef;
};

const StackedHeaderCell = ({ colDef }: HeaderProps) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%", textAlign: "center", px: 0.5 }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "1rem",
          lineHeight: 1.3,
          whiteSpace: "normal",
          wordBreak: "break-word",
          fontWeight: 500,
        }}
      >
        {colDef.headerName}
      </Typography>
    </Box>
  );
};

const TaskGrid: React.FC<TaskGridProps> = ({
  rows,
  baseColumns,
  columnOptions,
  taskKey,
  rowClassName, // ← add this
}) => {
  const [columnVisibility, setColumnVisibility] = useState(() => {
    return (
      JSON.parse(
        localStorage.getItem(`${taskKey}ColumnVisibility`) || "null",
      ) ??
      columnOptions.reduce(
        (acc, col) => ({ ...acc, [col.field]: true }),
        {} as Record<string, boolean>,
      )
    );
  });
  const context = useContext(UserContext);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => {
      try {
        const stored = localStorage.getItem(`${taskKey}ColumnWidths`);
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    },
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleOpenColumnMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseColumnMenu = () => setAnchorEl(null);

  const columnsWithCustomHeaders = baseColumns.map((col) => {
    const storedWidth = columnWidths[col.field];
    const hasStoredWidth = typeof storedWidth === "number";

    return {
      ...col,
      width: hasStoredWidth ? storedWidth : col.width,
      ...(hasStoredWidth ? { flex: undefined } : {}),

      sortable: col.field === "cropName" || col.field === "productName",
      disableColumnMenu: true,
      renderHeader:
        col.renderHeader ??
        ((params) => <StackedHeaderCell colDef={params.colDef} />),
    };
  });

  // ---- NEW: Page-scope batch → unique color map ----

  // 1. Extract batches in *visible row order* (NO sorting)
  const batchesInOrder = rows
    .map((r) => r.batchNumber)
    .filter((b): b is string => !!b && typeof b === "string");

  // 2. Remove duplicates while keeping first appearance
  const uniqueBatches: string[] = [];
  for (const b of batchesInOrder) {
    if (!uniqueBatches.includes(b)) uniqueBatches.push(b);
  }

  // 3. Assign colors sequentially from your interleaved pastel palette
  const palette = BASE_PASTEL_COLORS;

  const pageColorMap: Record<string, string> = {};
  uniqueBatches.forEach((batch, index) => {
    pageColorMap[batch] = palette[index % palette.length];
  });

  // 4. Build CSS rules
  const batchStyles = uniqueBatches
    .map((batch) => {
      const cls = `batch-${batch.replace(/[^a-zA-Z0-9]/g, "_")}`;
      const color = pageColorMap[batch];
      return `
      .MuiDataGrid-row.${cls} {
        background-color: ${color} !important;
      }
    `;
    })
    .join("\n");

  return (
    <Box sx={{ width: "100%" }}>
      <style>{batchStyles}</style>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          onClick={handleOpenColumnMenu}
          size="small"
          startIcon={<ViewColumnIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
        >
          Show/Hide
        </Button>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ fontSize: "0.85rem" }}>
            <List dense>
              {columnOptions.map((col) => (
                <ListItem
                  key={col.field}
                  onClick={() => {
                    const updated = {
                      ...columnVisibility,
                      [col.field]: !columnVisibility[col.field],
                    };
                    setColumnVisibility(updated);
                    localStorage.setItem(
                      `${taskKey}ColumnVisibility`,
                      JSON.stringify(updated),
                    );
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={columnVisibility[col.field] !== false}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={col.label}
                    primaryTypographyProps={{ fontSize: "0.85rem" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Popover>
      </Box>

      <DataGrid
        autoHeight
        hideFooterPagination
        paginationModel={{ pageSize: 100, page: 0 }}
        pageSizeOptions={[]}
        rows={rows}
        columns={columnsWithCustomHeaders}
        hideFooter
        columnVisibilityModel={{
          ...columnVisibility,
          isPastDue: false,
        }}
        getRowClassName={(params) => {
          const row = params.row;
          const grower = context.grower;
          const today = new Date().toISOString().slice(0, 10);
          if (!row.completed && row.scheduledDate < today) {
            return "overdue-row";
          }

          // ⛔ NEW: Disable batch colors if toggle is off
          if (!context.grower?.ShowBatchColors) {
            return "";
          }

          // Batch class applied from parent
          if (typeof rowClassName === "function") {
            return rowClassName(params) || "";
          }

          return "";
        }}
        onColumnVisibilityModelChange={(newModel) => {
          setColumnVisibility(newModel);
          localStorage.setItem(
            `${taskKey}ColumnVisibility`,
            JSON.stringify(newModel),
          );
        }}
        onColumnWidthChange={(params: GridColumnResizeParams) => {
          const field = params.colDef.field;
          const width = params.width;

          if (!field || typeof width !== "number") return;

          const updated = { ...columnWidths, [field]: width };
          setColumnWidths(updated);
          localStorage.setItem(
            `${taskKey}ColumnWidths`,
            JSON.stringify(updated),
          );
        }}
        sx={{
          ...gridStyle,
          transform: "none",
        }}
      />
    </Box>
  );
};

export default TaskGrid;
